<template>
  <div class="product-edit-page">
    <div class="sticky-bar">
      <div>
        <div class="sticky-bar__title">{{ isEdit ? '编辑商品' : '新增商品' }}</div>
        <div class="sticky-bar__desc">按基础信息、图片、规格、团购配置的顺序完成录入，保存失败会自动定位到对应区块。</div>
      </div>
      <div class="sticky-bar__actions">
        <el-tag v-if="isDirty()" size="mini" type="warning" effect="plain">有未保存修改</el-tag>
        <el-button @click="backToList">取消</el-button>
        <el-button type="primary" :loading="submitting" @click="submit">保存商品</el-button>
      </div>
    </div>

    <div v-loading="pageLoading" class="page-layout">
      <div class="main-column">
        <el-card ref="basicSection" shadow="never" class="section-card" :class="{ 'section-card--focus': focusedSection === 'basicSection' }">
          <div slot="header" class="section-card__header">
            <span>基础信息</span>
            <el-button type="text" class="section-card__toggle" @click="toggleSection('basicSection')">
              {{ isSectionCollapsed('basicSection') ? '展开' : '收起' }}
            </el-button>
          </div>
          <el-form v-show="!isSectionCollapsed('basicSection')" ref="formRef" :model="form" :rules="formRules" label-width="110px">
            <el-form-item label="商品名称" prop="name">
              <el-input v-model.trim="form.name" maxlength="200" show-word-limit />
            </el-form-item>
            <el-form-item label="副标题">
              <el-input v-model.trim="form.subTitle" maxlength="255" show-word-limit />
            </el-form-item>
            <el-form-item label="商品分类" prop="categoryId">
              <el-select v-model="form.categoryId" placeholder="请选择商品分类" style="width: 100%">
                <el-option v-for="item in categories" :key="item.id" :label="item.name" :value="item.id" />
              </el-select>
            </el-form-item>
            <el-form-item label="主图地址" prop="mainImage">
              <div class="upload-inline">
                <el-input v-model.trim="form.mainImage" placeholder="请输入商品主图地址" />
                <el-upload :show-file-list="false" :http-request="uploadMainImage" accept="image/*" action="#">
                  <el-button :loading="uploadLoading.mainImage">上传图片</el-button>
                </el-upload>
              </div>
            </el-form-item>
            <el-form-item label="排序" prop="sort">
              <el-input-number v-model="form.sort" :min="0" :precision="0" />
            </el-form-item>
            <el-form-item label="商品状态">
              <el-switch v-model="form.status" :active-value="1" :inactive-value="0" />
            </el-form-item>
            <el-form-item label="商品详情">
              <el-input
                v-model="form.detailContent"
                type="textarea"
                :rows="6"
                placeholder="可填写商品介绍、适用说明、服务说明等"
              />
            </el-form-item>
          </el-form>
        </el-card>

        <el-card ref="imageSection" shadow="never" class="section-card" :class="{ 'section-card--focus': focusedSection === 'imageSection' }">
          <div slot="header" class="section-card__header">
            <span>图片管理</span>
            <el-button type="text" class="section-card__toggle" @click="toggleSection('imageSection')">
              {{ isSectionCollapsed('imageSection') ? '展开' : '收起' }}
            </el-button>
          </div>
          <template v-if="!isSectionCollapsed('imageSection')">
            <el-form label-width="110px">
              <el-form-item label="轮播图">
                <div v-for="(item, index) in bannerImages" :key="`banner-${index}`" class="image-row">
                  <el-input v-model.trim="bannerImages[index]" placeholder="请输入轮播图地址" />
                  <el-upload :show-file-list="false" :http-request="options => uploadListImage('banner', index, options)" accept="image/*" action="#">
                    <el-button :loading="uploadLoading[`banner-${index}`]">上传</el-button>
                  </el-upload>
                  <el-button type="text" :disabled="index === 0" @click="moveImage('banner', index, -1)">上移</el-button>
                  <el-button type="text" :disabled="index === bannerImages.length - 1" @click="moveImage('banner', index, 1)">下移</el-button>
                  <el-button type="text" @click="removeImage('banner', index)">删除</el-button>
                </div>
                <el-button type="primary" plain @click="bannerImages.push('')">新增轮播图</el-button>
              </el-form-item>
              <el-form-item label="详情图">
                <div v-for="(item, index) in detailImages" :key="`detail-${index}`" class="image-row">
                  <el-input v-model.trim="detailImages[index]" placeholder="请输入详情图地址" />
                  <el-upload :show-file-list="false" :http-request="options => uploadListImage('detail', index, options)" accept="image/*" action="#">
                    <el-button :loading="uploadLoading[`detail-${index}`]">上传</el-button>
                  </el-upload>
                  <el-button type="text" :disabled="index === 0" @click="moveImage('detail', index, -1)">上移</el-button>
                  <el-button type="text" :disabled="index === detailImages.length - 1" @click="moveImage('detail', index, 1)">下移</el-button>
                  <el-button type="text" @click="removeImage('detail', index)">删除</el-button>
                </div>
                <el-button type="primary" plain @click="detailImages.push('')">新增详情图</el-button>
              </el-form-item>
            </el-form>

            <div class="image-preview-block">
              <div class="image-preview-block__title">轮播图预览</div>
              <div v-if="validBannerImages.length" class="image-preview-grid">
                <el-image
                  v-for="(image, index) in validBannerImages"
                  :key="`banner-preview-${index}`"
                  class="image-preview"
                  :src="image"
                  fit="cover"
                  :preview-src-list="validBannerImages"
                >
                  <div slot="error" class="image-preview__error">预览失败</div>
                </el-image>
              </div>
              <div v-else class="image-preview-empty">暂无轮播图</div>

              <div class="image-preview-block__title image-preview-block__title--detail">详情图预览</div>
              <div v-if="validDetailImages.length" class="detail-preview-list">
                <el-image
                  v-for="(image, index) in validDetailImages"
                  :key="`detail-preview-${index}`"
                  class="detail-preview"
                  :src="image"
                  fit="cover"
                  :preview-src-list="validDetailImages"
                >
                  <div slot="error" class="image-preview__error">预览失败</div>
                </el-image>
              </div>
              <div v-else class="image-preview-empty">暂无详情图</div>
            </div>
          </template>
        </el-card>

        <el-card ref="skuSection" shadow="never" class="section-card" :class="{ 'section-card--focus': focusedSection === 'skuSection' }">
          <div slot="header" class="section-card__header section-card__header--split">
            <span>规格与 SKU</span>
            <div class="section-card__header-actions">
              <el-button v-show="!isSectionCollapsed('skuSection')" type="primary" plain @click="generateSkuList">生成 SKU 组合</el-button>
              <el-button type="text" class="section-card__toggle" @click="toggleSection('skuSection')">
                {{ isSectionCollapsed('skuSection') ? '展开' : '收起' }}
              </el-button>
            </div>
          </div>

          <template v-if="!isSectionCollapsed('skuSection')">
            <div class="spec-intro">
              先定义规格维度和规格值，例如“颜色 / 绿、白”或“班型 / 周末班、暑假班”，再自动生成 SKU 组合。
            </div>

            <div v-for="(axis, index) in form.specAxes" :key="`axis-${index}`" class="axis-card">
              <el-input v-model.trim="axis.name" placeholder="规格名，例如：颜色、班型、课时" />
              <el-input
                v-model.trim="axis.valuesText"
                placeholder="规格值，多个值用中文逗号或英文逗号分隔，例如：绿，白"
              />
              <el-button type="text" @click="removeSpecAxis(index)">删除</el-button>
            </div>

            <div class="axis-actions">
              <el-button type="primary" plain @click="addSpecAxis">新增规格维度</el-button>
            </div>

            <div class="spec-preview">
              <div class="spec-preview__title">规格预览</div>
              <div v-if="normalizedSpecAxes.length" class="spec-preview__list">
                <div v-for="axis in normalizedSpecAxes" :key="axis.name" class="spec-preview__item">
                  <strong>{{ axis.name }}</strong>
                  <span>{{ axis.values.join(' / ') }}</span>
                </div>
              </div>
              <div v-else class="spec-preview__empty">还没有配置规格维度</div>
            </div>

            <el-alert
              v-if="form.groupBuyEnabled"
              class="mode-alert mode-alert--sku"
              :title="skuModeTipTitle"
              :description="skuModeTipDescription"
              :type="form.groupBuyPriceMode === 'SKU' ? 'warning' : 'info'"
              :closable="false"
              show-icon
            />

            <div class="sku-area">
              <div class="sku-area__head">
                <span>SKU 列表</span>
                <span class="sku-area__tip">规格变更后，请重新点击“生成 SKU 组合”同步 SKU。</span>
              </div>

              <div v-if="form.skuList.length" class="sku-batch-panel">
                <div class="sku-batch-panel__title">
                  批量设置
                  <span v-if="form.groupBuyEnabled && form.groupBuyPriceMode === 'SKU'" class="sku-batch-panel__tag">当前可批量设置 SKU 团购价</span>
                </div>
                <div v-if="form.groupBuyEnabled && form.groupBuyPriceMode === 'SKU'" class="sku-batch-panel__desc">
                  这里填写的团购价会批量应用到全部 SKU，如果当前是统一团购价模式，这一栏不会生效。
                </div>
                <div class="sku-batch-grid">
                  <el-input v-model.trim="batchSkuForm.price" placeholder="批量售价" />
                  <el-input v-model.trim="batchSkuForm.originalPrice" placeholder="批量划线价" />
                  <el-input v-model.trim="batchSkuForm.stock" placeholder="批量库存" />
                  <el-select v-model="batchSkuForm.status" placeholder="批量状态" clearable>
                    <el-option label="上架" :value="1" />
                    <el-option label="下架" :value="0" />
                  </el-select>
                  <el-input
                    v-if="form.groupBuyEnabled && form.groupBuyPriceMode === 'SKU'"
                    v-model.trim="batchSkuForm.groupBuyPrice"
                    placeholder="批量团购价"
                  />
                  <div class="sku-batch-panel__actions">
                    <el-button type="primary" plain @click="applyBatchSkuFields">应用到全部 SKU</el-button>
                    <el-button @click="resetBatchSkuFields">清空批量栏</el-button>
                  </div>
                </div>
              </div>

              <div v-if="form.skuList.length" class="sku-table">
                <div
                  v-for="(sku, index) in form.skuList"
                  :key="sku._tempKey || sku.id || index"
                  ref="skuCards"
                  class="sku-card"
                  :class="{
                    'sku-card--focus': errorSkuIndex === index,
                    'sku-card--group-active': form.groupBuyEnabled && form.groupBuyPriceMode === 'SKU'
                  }"
                >
                  <div class="sku-card__top">
                    <div>
                      <div class="sku-card__title">{{ sku.skuName || `SKU ${index + 1}` }}</div>
                      <div class="sku-card__spec">{{ specSummary(sku.specItems) }}</div>
                    </div>
                    <div class="sku-card__actions">
                      <el-tag v-if="form.groupBuyEnabled && form.groupBuyPriceMode === 'SKU'" size="mini" type="warning" effect="plain">SKU 团购价生效</el-tag>
                      <el-tag v-else-if="form.groupBuyEnabled" size="mini" effect="plain">统一团购价生效</el-tag>
                      <el-button type="text" @click="fillBatchFromSku(index)">带入批量栏</el-button>
                      <el-button type="text" @click="removeSku(index)">删除</el-button>
                    </div>
                  </div>

                  <el-row :gutter="12">
                    <el-col :span="12">
                      <el-input v-model.trim="sku.skuName" placeholder="SKU 名称" />
                    </el-col>
                    <el-col :span="12">
                      <el-input v-model.trim="sku.skuCode" placeholder="SKU 编码，可留空自动生成" />
                    </el-col>
                  </el-row>

                  <el-row :gutter="12" style="margin-top: 12px">
                    <el-col :span="6">
                      <el-input v-model.trim="sku.price" placeholder="售价" />
                    </el-col>
                    <el-col :span="6">
                      <el-input v-model.trim="sku.originalPrice" placeholder="划线价（可留空）" />
                    </el-col>
                    <el-col :span="6">
                      <el-input v-model.trim="sku.stock" placeholder="库存" />
                    </el-col>
                    <el-col :span="6">
                      <el-switch v-model="sku.status" :active-value="1" :inactive-value="0" />
                    </el-col>
                  </el-row>

                  <el-row v-if="form.groupBuyEnabled && form.groupBuyPriceMode === 'SKU'" :gutter="12" style="margin-top: 12px">
                    <el-col :span="12">
                      <div class="sku-group-price-field sku-group-price-field--active">
                        <el-input
                          v-model.trim="sku.groupBuyPrice"
                          class="sku-group-price-input"
                          placeholder="该 SKU 团购价"
                        />
                      </div>
                    </el-col>
                    <el-col :span="12" class="sku-card__hint">
                      当前为“按 SKU 单独定价”模式，这里填写的团购价才会生效，且必须低于当前 SKU 售价。
                    </el-col>
                  </el-row>

                  <el-row v-else-if="form.groupBuyEnabled" :gutter="12" style="margin-top: 12px">
                    <el-col :span="24" class="sku-card__hint sku-card__hint--muted">
                      当前为“统一团购价”模式，本 SKU 不单独填写团购价，最终统一使用下方团购配置中的价格。
                    </el-col>
                  </el-row>

                  <div class="upload-inline" style="margin-top: 12px">
                    <el-input v-model.trim="sku.image" placeholder="SKU 图片地址" />
                    <el-upload :show-file-list="false" :http-request="options => uploadSkuImage(index, options)" accept="image/*" action="#">
                      <el-button :loading="uploadLoading[`sku-${index}`]">上传图片</el-button>
                    </el-upload>
                  </div>
                  <el-image
                    v-if="sku.image"
                    class="sku-image-preview"
                    :src="sku.image"
                    fit="cover"
                    :preview-src-list="[sku.image]"
                  >
                    <div slot="error" class="image-preview__error">预览失败</div>
                  </el-image>
                </div>
              </div>
              <div v-else class="sku-empty">请先配置规格维度，再生成 SKU 组合。</div>
            </div>
          </template>
        </el-card>

        <el-card ref="groupSection" shadow="never" class="section-card" :class="{ 'section-card--focus': focusedSection === 'groupSection' }">
          <div slot="header" class="section-card__header section-card__header--split">
            <span>团购配置</span>
            <div class="section-card__header-actions">
              <el-switch v-show="!isSectionCollapsed('groupSection')" v-model="form.groupBuyEnabled" />
              <el-button type="text" class="section-card__toggle" @click="toggleSection('groupSection')">
                {{ isSectionCollapsed('groupSection') ? '展开' : '收起' }}
              </el-button>
            </div>
          </div>

          <template v-if="!isSectionCollapsed('groupSection')">
            <el-alert
              v-if="form.groupBuyEnabled"
              class="mode-alert"
              :title="groupBuyModeTipTitle"
              :description="groupBuyModeTipDescription"
              :type="form.groupBuyPriceMode === 'SKU' ? 'warning' : 'info'"
              :closable="false"
              show-icon
            />

            <el-form label-width="110px">
              <el-form-item label="活动标题">
                <el-input v-model.trim="form.groupBuyTitle" :disabled="!form.groupBuyEnabled" />
              </el-form-item>
              <el-form-item label="价格模式">
                <el-radio-group v-model="form.groupBuyPriceMode" :disabled="!form.groupBuyEnabled">
                  <el-radio label="UNIFIED">统一团购价</el-radio>
                  <el-radio label="SKU">按 SKU 单独定价</el-radio>
                </el-radio-group>
              </el-form-item>
              <el-form-item label="发起方式">
                <el-radio-group v-model="form.groupBuyLaunchMode" :disabled="!form.groupBuyEnabled">
                  <el-radio label="USER">用户可发起</el-radio>
                  <el-radio label="PLATFORM">平台发起</el-radio>
                </el-radio-group>
                <div class="form-tip">平台发起时，小程序端只能选择已有平台团参团，不能发起新拼团。</div>
              </el-form-item>
              <el-form-item v-if="form.groupBuyPriceMode === 'UNIFIED'" label="团购价格">
                <el-input v-model.trim="form.groupBuyPrice" :disabled="!form.groupBuyEnabled" />
                <div class="form-tip">统一团购价模式下，所有 SKU 都会使用这里填写的团购价。</div>
              </el-form-item>
              <el-alert
                v-if="form.groupBuyEnabled && form.groupBuyPriceMode === 'SKU'"
                title="当前为按 SKU 单独定价模式"
                description="请回到上方 SKU 卡片中逐个填写团购价，未填写团购价的 SKU 无法通过保存校验。"
                type="warning"
                :closable="false"
                show-icon
              />
              <el-form-item label="成团人数" style="margin-top: 16px">
                <el-input-number v-model="form.groupBuyPeopleCount" :disabled="!form.groupBuyEnabled" :min="2" :precision="0" />
              </el-form-item>
              <el-form-item label="开始时间">
                <el-date-picker v-model="form.groupBuyStartTime" :disabled="!form.groupBuyEnabled" type="datetime" value-format="yyyy-MM-ddTHH:mm:ss" />
              </el-form-item>
              <el-form-item label="结束时间">
                <el-date-picker v-model="form.groupBuyEndTime" :disabled="!form.groupBuyEnabled" type="datetime" value-format="yyyy-MM-ddTHH:mm:ss" />
              </el-form-item>
            </el-form>
          </template>
        </el-card>
      </div>

      <div class="side-column">
        <el-card shadow="never" class="quick-nav-card">
          <div slot="header">快速定位</div>
          <div class="quick-nav">
            <button type="button" class="quick-nav__item" @click="scrollToSection('basicSection')">基础信息</button>
            <button type="button" class="quick-nav__item" @click="scrollToSection('imageSection')">图片管理</button>
            <button type="button" class="quick-nav__item" @click="scrollToSection('skuSection')">规格 SKU</button>
            <button type="button" class="quick-nav__item" @click="scrollToSection('groupSection')">团购配置</button>
          </div>
        </el-card>

        <el-card shadow="never">
          <div slot="header">预览信息</div>
          <div class="preview-cover">
            <img v-if="form.mainImage" :src="form.mainImage" alt="主图预览">
            <div v-else class="preview-cover__empty">主图预览</div>
          </div>
          <div class="preview-title">{{ form.name || '商品名称' }}</div>
          <div class="preview-subtitle">{{ form.subTitle || '这里显示副标题' }}</div>
          <div class="preview-price">￥{{ previewMinPrice }} - ￥{{ previewMaxPrice }}</div>
          <div v-if="form.groupBuyEnabled && previewGroupBuyPrice" class="preview-group-price">
            团购价 ￥{{ formatPrice(previewGroupBuyPrice) }}
          </div>
          <div v-if="form.groupBuyEnabled" class="preview-mode-tip">{{ previewGroupBuyModeText }}</div>
          <div class="preview-meta">SKU 数量：{{ form.skuList.length }}</div>
          <div class="preview-meta">状态：{{ Number(form.status) === 1 ? '上架' : '下架' }}</div>
        </el-card>
      </div>
    </div>
  </div>
</template>

<script>
import { addProduct, getCategoryList, getProductDetail, updateProduct, uploadAdminImage } from '../../api/admin'
import { formatPrice } from '../../utils/format'

const MONEY_PATTERN = /^(0|[1-9]\d*)(\.\d{1,2})?$/

const createSpecAxis = () => ({ name: '', valuesText: '' })

const createSku = () => ({
  id: null,
  _tempKey: `${Date.now()}_${Math.random().toString(16).slice(2, 8)}`,
  skuName: '',
  skuCode: '',
  specJson: '',
  specItems: [],
  price: '',
  originalPrice: '',
  stock: '',
  image: '',
  groupBuyPrice: '',
  status: 1
})

const createBatchSkuForm = () => ({
  price: '',
  originalPrice: '',
  stock: '',
  status: '',
  groupBuyPrice: ''
})

const createForm = () => ({
  id: null,
  categoryId: '',
  name: '',
  subTitle: '',
  mainImage: '',
  status: 1,
  sort: 1,
  detailContent: '',
  groupBuyEnabled: false,
  groupBuyTitle: '',
  groupBuyPriceMode: 'UNIFIED',
  groupBuyLaunchMode: 'USER',
  groupBuyPrice: '',
  groupBuyPeopleCount: 2,
  groupBuyStartTime: '',
  groupBuyEndTime: '',
  specAxes: [createSpecAxis()],
  skuList: []
})

function parseSpecJson(value) {
  if (!value) return []
  try {
    const parsed = JSON.parse(value)
    return Array.isArray(parsed)
      ? parsed
          .map(item => ({
            name: `${item?.name || ''}`.trim(),
            value: `${item?.value || ''}`.trim()
          }))
          .filter(item => item.name && item.value)
      : []
  } catch (error) {
    return []
  }
}

function buildSpecJson(items) {
  return JSON.stringify(items.map(item => ({ name: item.name, value: item.value })))
}

function normalizeAxes(axes) {
  return axes
    .map(axis => ({
      name: `${axis?.name || ''}`.trim(),
      values: `${axis?.valuesText || ''}`
        .split(/[，,]/)
        .map(item => item.trim())
        .filter(Boolean)
        .filter((item, index, arr) => arr.indexOf(item) === index)
    }))
    .filter(axis => axis.name && axis.values.length)
}

function cartesianSpecItems(axes, index = 0, current = []) {
  if (index >= axes.length) return [current]
  const axis = axes[index]
  return axis.values.flatMap(value =>
    cartesianSpecItems(axes, index + 1, [...current, { name: axis.name, value }])
  )
}

function specKey(specItems) {
  return specItems.map(item => `${item.name}:${item.value}`).join('|')
}

function axisTextFromSkuList(skuList) {
  if (!Array.isArray(skuList) || !skuList.length) return [createSpecAxis()]
  const axisMap = new Map()
  skuList.forEach(sku => {
    parseSpecJson(sku.specJson).forEach(spec => {
      if (!axisMap.has(spec.name)) axisMap.set(spec.name, [])
      const values = axisMap.get(spec.name)
      if (!values.includes(spec.value)) values.push(spec.value)
    })
  })
  const axes = [...axisMap.entries()].map(([name, values]) => ({
    name,
    valuesText: values.join('，')
  }))
  return axes.length ? axes : [createSpecAxis()]
}

export default {
  props: {
    id: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      pageLoading: false,
      submitting: false,
      categories: [],
      form: createForm(),
      bannerImages: [''],
      detailImages: [''],
      batchSkuForm: createBatchSkuForm(),
      uploadLoading: {},
      focusedSection: '',
      errorSkuIndex: null,
      collapsedSections: {
        basicSection: false,
        imageSection: false,
        skuSection: false,
        groupSection: false
      },
      initialSnapshot: '',
      skipLeaveConfirm: false,
      formRules: {
        name: [{ required: true, message: '请输入商品名称', trigger: 'blur' }],
        categoryId: [{ required: true, message: '请选择商品分类', trigger: 'change' }],
        mainImage: [{ required: true, message: '请输入主图地址', trigger: 'blur' }],
        sort: [{ required: true, message: '请输入排序值', trigger: 'change' }]
      }
    }
  },
  computed: {
    isEdit() {
      return Boolean(this.id)
    },
    normalizedSpecAxes() {
      return normalizeAxes(this.form.specAxes)
    },
    previewMinPrice() {
      const prices = this.form.skuList.map(item => Number(item.price)).filter(item => !Number.isNaN(item))
      return formatPrice(prices.length ? Math.min(...prices) : 0)
    },
    previewMaxPrice() {
      const prices = this.form.skuList.map(item => Number(item.price)).filter(item => !Number.isNaN(item))
      return formatPrice(prices.length ? Math.max(...prices) : 0)
    },
    previewGroupBuyPrice() {
      if (!this.form.groupBuyEnabled) return ''
      if (this.form.groupBuyPriceMode === 'SKU') {
        const prices = this.form.skuList.map(item => Number(item.groupBuyPrice)).filter(item => !Number.isNaN(item) && item > 0)
        return prices.length ? Math.min(...prices) : ''
      }
      return this.form.groupBuyPrice
    },
    validBannerImages() {
      return this.normalizeImageList(this.bannerImages)
    },
    validDetailImages() {
      return this.normalizeImageList(this.detailImages)
    },
    groupBuyModeTipTitle() {
      return this.form.groupBuyPriceMode === 'SKU' ? '当前为按 SKU 单独定价模式' : '当前为统一团购价模式'
    },
    groupBuyModeTipDescription() {
      return this.form.groupBuyPriceMode === 'SKU'
        ? '团购价需要在上方每个 SKU 卡片中单独填写，未填写团购价的 SKU 无法保存。'
        : '所有 SKU 都会统一使用这里填写的团购价，上方 SKU 卡片中的团购价字段不会生效。'
    },
    skuModeTipTitle() {
      return this.form.groupBuyPriceMode === 'SKU' ? 'SKU 卡片中的团购价字段当前生效' : 'SKU 卡片中的团购价字段当前不生效'
    },
    skuModeTipDescription() {
      return this.form.groupBuyPriceMode === 'SKU'
        ? '请在每个 SKU 卡片中填写对应团购价，也可以先用批量栏统一填充后再逐条微调。'
        : '当前采用统一团购价模式，SKU 卡片只维护售价、划线价和库存，团购价请在下方团购配置区填写。'
    },
    previewGroupBuyModeText() {
      return this.form.groupBuyPriceMode === 'SKU'
        ? '当前预览显示的是所有 SKU 团购价中的最低值，实际以前端所选 SKU 为准。'
        : '当前预览显示的是统一团购价，所有 SKU 都会按这一价格参与团购。'
    }
  },
  async created() {
    this.pageLoading = true
    try {
      this.categories = await getCategoryList()
      if (this.isEdit) {
        await this.loadDetail()
      } else {
        this.markClean()
      }
    } finally {
      this.pageLoading = false
    }
  },
  mounted() {
    window.addEventListener('beforeunload', this.handleBeforeUnload)
  },
  beforeDestroy() {
    window.removeEventListener('beforeunload', this.handleBeforeUnload)
  },
  beforeRouteLeave(to, from, next) {
    if (this.skipLeaveConfirm || !this.isDirty()) {
      this.skipLeaveConfirm = false
      next()
      return
    }
    this.$confirm('当前商品还有未保存的修改，确定现在离开吗？', '离开确认', {
      type: 'warning',
      confirmButtonText: '离开',
      cancelButtonText: '继续编辑'
    }).then(() => {
      this.skipLeaveConfirm = true
      next()
    }).catch(() => next(false))
  },
  methods: {
    formatPrice,
    handleBeforeUnload(event) {
      if (!this.isDirty()) return
      event.preventDefault()
      event.returnValue = ''
    },
    buildSnapshot() {
      return JSON.stringify({
        form: {
          ...this.form,
          skuList: this.form.skuList.map(item => ({
            id: item.id || null,
            skuName: item.skuName || '',
            skuCode: item.skuCode || '',
            specItems: (item.specItems || []).map(spec => ({
              name: spec.name,
              value: spec.value
            })),
            price: `${item.price ?? ''}`,
            originalPrice: `${item.originalPrice ?? ''}`,
            stock: `${item.stock ?? ''}`,
            image: `${item.image || ''}`.trim(),
            groupBuyPrice: `${item.groupBuyPrice ?? ''}`,
            status: Number(item.status || 0)
          })),
          specAxes: this.form.specAxes.map(axis => ({
            name: axis.name,
            valuesText: axis.valuesText
          }))
        },
        bannerImages: this.bannerImages,
        detailImages: this.detailImages
      })
    },
    markClean() {
      this.initialSnapshot = this.buildSnapshot()
    },
    isDirty() {
      return Boolean(this.initialSnapshot) && this.initialSnapshot !== this.buildSnapshot()
    },
    isSectionCollapsed(section) {
      return Boolean(this.collapsedSections[section])
    },
    toggleSection(section) {
      this.$set(this.collapsedSections, section, !this.isSectionCollapsed(section))
    },
    expandSection(section) {
      if (this.isSectionCollapsed(section)) {
        this.$set(this.collapsedSections, section, false)
      }
    },
    setFocusedSection(section, skuIndex = null) {
      this.focusedSection = section
      this.errorSkuIndex = typeof skuIndex === 'number' ? skuIndex : null
      clearTimeout(this.focusTimer)
      this.focusTimer = setTimeout(() => {
        this.focusedSection = ''
        this.errorSkuIndex = null
      }, 2500)
    },
    scrollToSection(refName, skuIndex = null) {
      this.expandSection(refName)
      this.setFocusedSection(refName, skuIndex)
      this.$nextTick(() => {
        if (refName === 'skuSection' && typeof skuIndex === 'number' && Array.isArray(this.$refs.skuCards)) {
          const targetCard = this.$refs.skuCards[skuIndex]
          if (targetCard && targetCard.scrollIntoView) {
            targetCard.scrollIntoView({ behavior: 'smooth', block: 'center' })
            return
          }
        }
        const target = this.$refs[refName]
        const element = target && target.$el ? target.$el : target
        if (element && element.scrollIntoView) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
      })
    },
    handleValidationError(error) {
      if (!error) return
      this.$message.error(error.message)
      this.scrollToSection(error.section || 'basicSection', error.skuIndex)
    },
    async loadDetail() {
      const detail = await getProductDetail(this.id)
      this.form = {
        ...createForm(),
        ...detail,
        categoryId: detail.categoryId ? Number(detail.categoryId) : '',
        groupBuyEnabled: Boolean(detail.groupBuyEnabled),
        groupBuyPriceMode: detail.groupBuyPriceMode || 'UNIFIED',
        groupBuyLaunchMode: detail.groupBuyLaunchMode || 'USER',
        groupBuyPrice: detail.groupBuyPrice || '',
        groupBuyPeopleCount: detail.groupBuyPeopleCount || 2,
        groupBuyStartTime: detail.groupBuyStartTime || '',
        groupBuyEndTime: detail.groupBuyEndTime || '',
        specAxes: axisTextFromSkuList(detail.skuList || []),
        skuList: (detail.skuList || []).map(item => ({
          ...createSku(),
          ...item,
          price: `${item.price ?? ''}`,
          originalPrice: item.originalPrice == null || Number(item.originalPrice) <= 0 ? '' : `${item.originalPrice}`,
          stock: `${item.stock ?? ''}`,
          groupBuyPrice: `${item.groupBuyPrice ?? ''}`,
          specItems: parseSpecJson(item.specJson)
        }))
      }
      this.bannerImages = (detail.bannerImages || []).length ? [...detail.bannerImages] : ['']
      this.detailImages = (detail.detailImages || []).length ? [...detail.detailImages] : ['']
      this.markClean()
    },
    addSpecAxis() {
      this.form.specAxes.push(createSpecAxis())
    },
    removeSpecAxis(index) {
      if (this.form.specAxes.length === 1) {
        this.form.specAxes = [createSpecAxis()]
        return
      }
      this.form.specAxes.splice(index, 1)
    },
    generateSkuList() {
      const axes = this.normalizedSpecAxes
      if (!axes.length) {
        this.handleValidationError({
          section: 'skuSection',
          message: '请至少配置一个完整的规格维度'
        })
        return
      }
      const combinations = cartesianSpecItems(axes)
      const existingMap = new Map(this.form.skuList.map(item => [specKey(item.specItems || []), item]))
      this.form.skuList = combinations.map(items => {
        const key = specKey(items)
        const existed = existingMap.get(key)
        const skuName = items.map(item => item.value).join(' / ')
        return existed
          ? { ...existed, specItems: items, specJson: buildSpecJson(items), skuName: existed.skuName || skuName }
          : { ...createSku(), specItems: items, specJson: buildSpecJson(items), skuName }
      })
      this.$message.success(`已生成 ${this.form.skuList.length} 个 SKU 组合`)
    },
    fillBatchFromSku(index) {
      const sku = this.form.skuList[index]
      if (!sku) return
      this.batchSkuForm = {
        price: `${sku.price ?? ''}`,
        originalPrice: `${sku.originalPrice ?? ''}`,
        stock: `${sku.stock ?? ''}`,
        status: Number.isInteger(Number(sku.status)) ? Number(sku.status) : '',
        groupBuyPrice: `${sku.groupBuyPrice ?? ''}`
      }
      this.$message.success('已将该 SKU 配置带入批量栏')
    },
    applyBatchSkuFields() {
      if (!this.form.skuList.length) {
        this.$message.warning('请先生成 SKU 组合')
        return
      }
      const hasAnyValue = ['price', 'originalPrice', 'stock', 'groupBuyPrice'].some(key => `${this.batchSkuForm[key]}`.trim() !== '')
        || this.batchSkuForm.status === 0
        || this.batchSkuForm.status === 1
      if (!hasAnyValue) {
        this.$message.warning('请先填写至少一个批量字段')
        return
      }
      this.form.skuList = this.form.skuList.map(item => ({
        ...item,
        price: `${this.batchSkuForm.price}`.trim() !== '' ? this.batchSkuForm.price : item.price,
        originalPrice: `${this.batchSkuForm.originalPrice}`.trim() !== '' ? this.batchSkuForm.originalPrice : item.originalPrice,
        stock: `${this.batchSkuForm.stock}`.trim() !== '' ? this.batchSkuForm.stock : item.stock,
        groupBuyPrice: `${this.batchSkuForm.groupBuyPrice}`.trim() !== '' ? this.batchSkuForm.groupBuyPrice : item.groupBuyPrice,
        status: this.batchSkuForm.status === 0 || this.batchSkuForm.status === 1 ? this.batchSkuForm.status : item.status
      }))
      this.$message.success('批量配置已应用到全部 SKU')
    },
    resetBatchSkuFields() {
      this.batchSkuForm = createBatchSkuForm()
    },
    removeSku(index) {
      if (this.form.skuList.length === 1) {
        this.$message.warning('至少需要保留一个 SKU')
        return
      }
      this.form.skuList.splice(index, 1)
      if (this.errorSkuIndex === index) {
        this.errorSkuIndex = null
      }
    },
    specSummary(items) {
      return (items || []).map(item => `${item.name}：${item.value}`).join(' / ') || '暂无规格'
    },
    async doUpload(file, key) {
      this.$set(this.uploadLoading, key, true)
      try {
        const result = await uploadAdminImage(file)
        return result.url
      } finally {
        this.$set(this.uploadLoading, key, false)
      }
    },
    async uploadMainImage(options) {
      try {
        this.form.mainImage = await this.doUpload(options.file, 'mainImage')
        options.onSuccess && options.onSuccess({}, options.file)
      } catch (error) {
        options.onError && options.onError(error)
      }
    },
    async uploadListImage(type, index, options) {
      try {
        const url = await this.doUpload(options.file, `${type}-${index}`)
        if (type === 'banner') {
          this.$set(this.bannerImages, index, url)
        } else {
          this.$set(this.detailImages, index, url)
        }
        options.onSuccess && options.onSuccess({}, options.file)
      } catch (error) {
        options.onError && options.onError(error)
      }
    },
    async uploadSkuImage(index, options) {
      try {
        const url = await this.doUpload(options.file, `sku-${index}`)
        this.$set(this.form.skuList[index], 'image', url)
        options.onSuccess && options.onSuccess({}, options.file)
      } catch (error) {
        options.onError && options.onError(error)
      }
    },
    moveImage(type, index, offset) {
      const list = type === 'banner' ? [...this.bannerImages] : [...this.detailImages]
      const target = index + offset
      if (target < 0 || target >= list.length) return
      const current = list[index]
      list.splice(index, 1)
      list.splice(target, 0, current)
      if (type === 'banner') this.bannerImages = list
      else this.detailImages = list
    },
    removeImage(type, index) {
      const list = type === 'banner' ? this.bannerImages : this.detailImages
      if (list.length === 1) {
        if (type === 'banner') this.bannerImages = ['']
        else this.detailImages = ['']
        return
      }
      list.splice(index, 1)
    },
    normalizeImageList(list) {
      return list.map(item => `${item || ''}`.trim()).filter(Boolean)
    },
    validateMoney(value, label) {
      if (value === '' || value === null || value === undefined) return `${label}不能为空`
      if (!MONEY_PATTERN.test(String(value))) return `${label}最多保留两位小数`
      return ''
    },
    validateOptionalMoney(value, label) {
      if (value === '' || value === null || value === undefined) return ''
      if (!MONEY_PATTERN.test(String(value))) return `${label}最多保留两位小数`
      return ''
    },
    validateSpecAxes() {
      const axes = this.normalizedSpecAxes
      if (!axes.length) {
        return { section: 'skuSection', message: '请至少配置一个规格维度' }
      }
      const axisNames = axes.map(item => item.name)
      if (axisNames.some((name, index) => axisNames.indexOf(name) !== index)) {
        return { section: 'skuSection', message: '规格维度名称不能重复' }
      }
      return null
    },
    validateSkuRows() {
      if (!this.form.skuList.length) {
        return { section: 'skuSection', message: '请先生成 SKU 组合' }
      }
      const keys = this.form.skuList.map(item => specKey(item.specItems || []))
      if (keys.some((key, index) => keys.indexOf(key) !== index)) {
        return { section: 'skuSection', message: '存在重复的规格组合，请重新生成 SKU', skuIndex: keys.findIndex((key, index) => keys.indexOf(key) !== index) }
      }
      for (let i = 0; i < this.form.skuList.length; i += 1) {
        const item = this.form.skuList[i]
        const skuLabel = item.skuName || `SKU ${i + 1}`
        const priceMessage = this.validateMoney(item.price, `SKU「${skuLabel}」的售价`)
        if (priceMessage) return { section: 'skuSection', skuIndex: i, message: priceMessage }
        const originalPriceMessage = this.validateOptionalMoney(item.originalPrice, `SKU「${skuLabel}」的划线价`)
        if (originalPriceMessage) return { section: 'skuSection', skuIndex: i, message: originalPriceMessage }
        if (`${item.originalPrice ?? ''}`.trim() !== '' && Number(item.originalPrice) < Number(item.price)) {
          return { section: 'skuSection', skuIndex: i, message: `SKU「${skuLabel}」的划线价不能低于售价` }
        }
        if (!Number.isInteger(Number(item.stock)) || Number(item.stock) < 0) {
          return { section: 'skuSection', skuIndex: i, message: `SKU「${skuLabel}」的库存必须是大于等于 0 的整数` }
        }
      }
      return null
    },
    validateGroupBuy() {
      if (!this.form.groupBuyEnabled) return null
      if (!Number.isInteger(Number(this.form.groupBuyPeopleCount)) || Number(this.form.groupBuyPeopleCount) < 2) {
        return { section: 'groupSection', message: '成团人数至少为 2' }
      }
      if (!this.form.groupBuyStartTime || !this.form.groupBuyEndTime) {
        return { section: 'groupSection', message: '请选择团购开始和结束时间' }
      }
      if (new Date(this.form.groupBuyEndTime).getTime() < new Date(this.form.groupBuyStartTime).getTime()) {
        return { section: 'groupSection', message: '团购结束时间不能早于开始时间' }
      }
      if (this.form.groupBuyPriceMode === 'SKU') {
        for (let i = 0; i < this.form.skuList.length; i += 1) {
          const item = this.form.skuList[i]
          const skuLabel = item.skuName || `SKU ${i + 1}`
          const groupBuyPriceMessage = this.validateMoney(item.groupBuyPrice, `SKU「${skuLabel}」的团购价`)
          if (groupBuyPriceMessage) return { section: 'skuSection', skuIndex: i, message: groupBuyPriceMessage }
          if (Number(item.groupBuyPrice) >= Number(item.price)) {
            return { section: 'skuSection', skuIndex: i, message: `SKU「${skuLabel}」的团购价必须低于售价` }
          }
        }
        return null
      }
      const groupBuyPriceMessage = this.validateMoney(this.form.groupBuyPrice, '团购价格')
      if (groupBuyPriceMessage) return { section: 'groupSection', message: groupBuyPriceMessage }
      const prices = this.form.skuList.map(item => Number(item.price)).filter(item => !Number.isNaN(item))
      if (prices.length && Number(this.form.groupBuyPrice) >= Math.min(...prices)) {
        return { section: 'groupSection', message: '团购价格必须低于商品最低售价' }
      }
      return null
    },
    async validateBaseForm() {
      try {
        await this.$refs.formRef.validate()
        return true
      } catch (invalidFields) {
        const firstField = invalidFields && Object.keys(invalidFields)[0]
        this.handleValidationError({
          section: 'basicSection',
          message: invalidFields[firstField][0].message
        })
        return false
      }
    },
    async submit() {
      const valid = await this.validateBaseForm()
      if (!valid) return

      const specMessage = this.validateSpecAxes()
      if (specMessage) {
        this.handleValidationError(specMessage)
        return
      }
      const skuMessage = this.validateSkuRows()
      if (skuMessage) {
        this.handleValidationError(skuMessage)
        return
      }
      const groupMessage = this.validateGroupBuy()
      if (groupMessage) {
        this.handleValidationError(groupMessage)
        return
      }

      const payload = {
        ...this.form,
        categoryId: Number(this.form.categoryId),
        sort: Number(this.form.sort || 0),
        groupBuyEnabled: Boolean(this.form.groupBuyEnabled),
        groupBuyPriceMode: this.form.groupBuyEnabled ? this.form.groupBuyPriceMode : 'UNIFIED',
        groupBuyLaunchMode: this.form.groupBuyEnabled ? this.form.groupBuyLaunchMode : 'USER',
        groupBuyPrice: this.form.groupBuyEnabled && this.form.groupBuyPriceMode === 'UNIFIED' ? Number(this.form.groupBuyPrice) : null,
        groupBuyPeopleCount: this.form.groupBuyEnabled ? Number(this.form.groupBuyPeopleCount) : null,
        groupBuyStartTime: this.form.groupBuyEnabled ? this.form.groupBuyStartTime : null,
        groupBuyEndTime: this.form.groupBuyEnabled ? this.form.groupBuyEndTime : null,
        bannerImages: this.normalizeImageList(this.bannerImages),
        detailImages: this.normalizeImageList(this.detailImages),
        skuList: this.form.skuList.map(item => ({
          id: item.id,
          skuName: item.skuName || item.specItems.map(spec => spec.value).join(' / '),
          skuCode: item.skuCode,
          specJson: buildSpecJson(item.specItems),
          price: Number(item.price),
          originalPrice: `${item.originalPrice ?? ''}`.trim() !== '' ? Number(item.originalPrice) : null,
          stock: Number(item.stock),
          image: `${item.image || ''}`.trim(),
          groupBuyPrice: this.form.groupBuyEnabled && this.form.groupBuyPriceMode === 'SKU' ? Number(item.groupBuyPrice) : null,
          status: Number(item.status || 0)
        }))
      }

      this.submitting = true
      try {
        if (this.isEdit) await updateProduct(payload)
        else await addProduct(payload)
        this.markClean()
        this.$message.success('商品保存成功')
        this.skipLeaveConfirm = true
        this.submitting = false
        this.backToList()
        return
      } catch (error) {
        this.$message.error(error?.message || '商品保存失败，请检查填写内容后重试')
      } finally {
        this.submitting = false
      }
    },
    async backToList() {
      if (this.submitting) return
      if (this.isDirty()) {
        try {
          await this.$confirm('当前商品还有未保存的修改，确定现在返回列表吗？', '返回确认', {
            type: 'warning',
            confirmButtonText: '返回列表',
            cancelButtonText: '继续编辑'
          })
        } catch (error) {
          return
        }
      }
      this.skipLeaveConfirm = true
      this.$router.push('/products')
    }
  }
}
</script>

<style scoped>
.product-edit-page { padding-bottom: 32px; }
.sticky-bar { position: sticky; top: 0; z-index: 20; display: flex; align-items: center; justify-content: space-between; margin-bottom: 20px; padding: 14px 18px; border: 1px solid #ebeef5; border-radius: 14px; background: rgba(255, 255, 255, 0.94); backdrop-filter: blur(12px); }
.sticky-bar__title { font-size: 18px; font-weight: 600; color: #1f2937; }
.sticky-bar__desc { margin-top: 4px; color: #6b7280; font-size: 12px; }
.sticky-bar__actions { display: flex; align-items: center; gap: 12px; }
.page-layout { display: grid; grid-template-columns: minmax(0, 1fr) 320px; gap: 20px; align-items: start; }
.section-card { margin-bottom: 20px; transition: border-color 0.2s ease, box-shadow 0.2s ease; }
.section-card--focus { border-color: #d97706; box-shadow: 0 0 0 2px rgba(217, 119, 6, 0.16); }
.section-card__header { display: flex; align-items: center; justify-content: space-between; gap: 12px; }
.section-card__header--split { display: flex; align-items: center; justify-content: space-between; gap: 12px; }
.section-card__header-actions { display: flex; align-items: center; gap: 12px; }
.section-card__toggle { padding: 0; color: #8b7355; }
.section-card__toggle:hover { color: #b95a2a; }
.upload-inline { display: flex; gap: 12px; align-items: center; }
.image-row { display: grid; grid-template-columns: minmax(0, 1fr) auto auto auto auto; gap: 10px; margin-bottom: 12px; align-items: center; }
.spec-intro { margin-bottom: 16px; color: #6b7280; line-height: 1.7; font-size: 13px; }
.axis-card { display: grid; grid-template-columns: 180px minmax(0, 1fr) auto; gap: 12px; margin-bottom: 12px; align-items: center; }
.axis-actions { margin-bottom: 16px; }
.spec-preview { padding: 14px 16px; border: 1px solid #ebeef5; border-radius: 12px; background: #fafaf9; }
.spec-preview__title { margin-bottom: 10px; font-weight: 600; color: #374151; }
.spec-preview__list { display: flex; flex-direction: column; gap: 8px; }
.spec-preview__item { display: flex; gap: 12px; color: #4b5563; }
.spec-preview__item strong { min-width: 72px; }
.spec-preview__empty { color: #9ca3af; }
.mode-alert { margin: 16px 0; }
.mode-alert--sku { margin-bottom: 18px; }
.sku-area { margin-top: 18px; }
.sku-area__head { display: flex; align-items: center; justify-content: space-between; margin-bottom: 12px; }
.sku-area__tip { color: #9ca3af; font-size: 12px; }
.sku-batch-panel { margin-bottom: 16px; padding: 14px; border: 1px solid #ebeef5; border-radius: 12px; background: #fafaf9; }
.sku-batch-panel__title { display: flex; align-items: center; gap: 10px; margin-bottom: 12px; font-size: 14px; font-weight: 600; color: #374151; }
.sku-batch-panel__tag { padding: 2px 8px; border-radius: 999px; background: #fff7ed; color: #c2410c; font-size: 12px; font-weight: 500; }
.sku-batch-panel__desc { margin-bottom: 12px; color: #8b7355; font-size: 12px; line-height: 1.6; }
.sku-batch-grid { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 12px; align-items: center; }
.sku-batch-panel__actions { display: flex; gap: 12px; grid-column: 1 / -1; }
.sku-table { display: flex; flex-direction: column; gap: 12px; }
.sku-card { padding: 14px; border: 1px solid #ebeef5; border-radius: 12px; background: #fff; transition: border-color 0.2s ease, box-shadow 0.2s ease; }
.sku-card--focus { border-color: #d97706; box-shadow: 0 0 0 2px rgba(217, 119, 6, 0.16); }
.sku-card--group-active { border-color: #f0dfd1; background: linear-gradient(180deg, #fffdfb 0%, #ffffff 100%); }
.sku-card__top { display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; margin-bottom: 12px; }
.sku-card__title { font-size: 15px; font-weight: 600; color: #1f2937; }
.sku-card__spec { margin-top: 6px; color: #6b7280; font-size: 13px; }
.sku-card__actions { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; justify-content: flex-end; }
.sku-group-price-field { position: relative; }
.sku-group-price-field::before { content: ''; position: absolute; inset: -6px; border-radius: 14px; background: linear-gradient(135deg, rgba(245, 158, 11, 0.08), rgba(217, 119, 6, 0.03)); opacity: 1; pointer-events: none; }
.sku-group-price-field--active ::v-deep .el-input__inner { border-color: #e5c9a8; background: #fffdf9; box-shadow: 0 0 0 2px rgba(217, 119, 6, 0.08); color: #8b6b45; font-weight: 500; }
.sku-group-price-field--active ::v-deep .el-input__inner::placeholder { color: #c9aa86; }
.sku-group-price-field--active ::v-deep .el-input__inner:focus { border-color: #d7b78e; box-shadow: 0 0 0 3px rgba(180, 83, 9, 0.1); }
.sku-card__hint { display: flex; align-items: center; min-height: 40px; color: #9a5b2a; font-size: 12px; line-height: 1.6; }
.sku-card__hint--muted { color: #8c7b67; }
.sku-image-preview { width: 96px; height: 96px; margin-top: 12px; border-radius: 12px; background: #f5f7fb; overflow: hidden; }
.sku-empty { padding: 24px 0; color: #9ca3af; text-align: center; }
.side-column { position: sticky; top: 88px; }
.quick-nav-card { margin-bottom: 16px; }
.quick-nav { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
.quick-nav__item { height: 36px; border: 1px solid #ebeef5; border-radius: 10px; background: #fafaf9; color: #374151; cursor: pointer; transition: all 0.16s ease; }
.quick-nav__item:hover { border-color: #b95a2a; color: #b95a2a; background: #fff7ed; }
.preview-cover { width: 100%; height: 240px; border-radius: 12px; overflow: hidden; background: #f5f7fb; }
.preview-cover img { width: 100%; height: 100%; object-fit: cover; }
.preview-cover__empty { display: flex; align-items: center; justify-content: center; width: 100%; height: 100%; color: #9ca3af; }
.preview-title { margin-top: 14px; font-size: 18px; font-weight: 600; color: #1f2937; }
.preview-subtitle { margin-top: 8px; color: #6b7280; line-height: 1.6; }
.preview-price { margin-top: 14px; color: #b45309; font-size: 20px; font-weight: 700; }
.preview-group-price { margin-top: 10px; color: #b95a2a; font-size: 14px; font-weight: 600; }
.preview-mode-tip { margin-top: 10px; padding: 10px 12px; border-radius: 10px; background: #faf7f2; color: #7c6144; font-size: 12px; line-height: 1.6; }
.preview-meta { margin-top: 8px; color: #6b7280; font-size: 13px; }
.form-tip { margin-top: 6px; color: #909399; font-size: 12px; line-height: 1.6; }
.image-preview-block { margin-top: 10px; padding: 14px; border: 1px dashed #d8d3c8; border-radius: 14px; background: #fafaf9; }
.image-preview-block__title { margin-bottom: 10px; color: #374151; font-weight: 600; }
.image-preview-block__title--detail { margin-top: 18px; }
.image-preview-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(104px, 1fr)); gap: 12px; }
.image-preview { width: 100%; height: 104px; border-radius: 12px; background: #f5f7fb; overflow: hidden; }
.detail-preview-list { display: grid; grid-template-columns: repeat(auto-fill, minmax(96px, 1fr)); gap: 12px; }
.detail-preview { width: 100%; height: 128px; border-radius: 12px; background: #f5f7fb; overflow: hidden; }
.image-preview__error { display: flex; align-items: center; justify-content: center; width: 100%; height: 100%; color: #9ca3af; font-size: 12px; }
.image-preview-empty { padding: 16px; border-radius: 12px; background: #fff; color: #9ca3af; text-align: center; }
</style>
